import { Container } from '@amelio/ui-library';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
    ObjectiveLoadErrorMessage,
    ObjectiveSectionLoader,
} from './components/ObjectivePageStates';
import ObjectivesActionsPanel from './components/ObjectivesActionsPanel';
import ObjectivesCycleHelp from './components/ObjectivesCycleHelp';
import ObjectivesHeader from './components/ObjectivesHeader';
import OwnerGroup from './components/OwnerGroup';
import { emptyListMessageKey } from './objectiveLabels';

import { useGetCompanyInformation } from '@/api/directory';
import { useGetCompanyPillarSummaries, useGetObjectivesWithDrafts } from '@/api/objectives';
import ObjectiveListToolbar from '@/components/objectives/ObjectiveListToolbar';
import {
    EMPTY_STATE_TEXT,
    OBJECTIVES_PAGE_LAYOUT,
    OWNER_LIST_PANEL,
} from '@/components/objectives/objectiveStyles';
import PillarCarousel from '@/components/objectives/PillarCarousel';
import PillarScopeBar from '@/components/objectives/PillarScopeBar';
import PillarsEmptyState from '@/components/objectives/PillarsEmptyState';
import PillarSummariesSkeleton from '@/components/objectives/PillarSummariesSkeleton';
import PillarSummaryCard from '@/components/objectives/PillarSummaryCard';
import { Permission } from '@/constants/Permission';
import { PillarScope } from '@/constants/PillarScope';
import { useUserPermissions } from '@/hooks/api/useUserPermissions';
import { useObjectiveAbilities } from '@/hooks/objectives/useObjectiveAbilities';
import { useObjectiveListLens } from '@/hooks/objectives/useObjectiveListLens';
import { usePeriodOptions, usePeriodParam } from '@/hooks/objectives/usePeriodOptions';
import { useStrategicVision } from '@/hooks/objectives/useStrategicVision';
import { pillarSummariesAmong } from '@/utils/pillarSummariesAmong';

export default function ObjectivesPage() {
    const { t } = useTranslation();
    const { can } = useUserPermissions();
    const { search } = useLocation();

    const strategicVision = useStrategicVision();
    const {
        canManageOthersObjectives,
        canCreateObjectives,
        canReadPillarSummaries,
        isLoading: isLoadingAbilities,
    } = useObjectiveAbilities();
    const { data: company } = useGetCompanyInformation();
    const {
        options: periodOptions,
        defaultPeriodId,
        isLoading: isLoadingPeriods,
        isError: arePeriodsInError,
    } = usePeriodOptions();
    // The server resolves the period into "attached to it, or on free dates overlapping it"; with
    // no period at all -- or a custom range still half-picked -- the lists simply run unfiltered.
    const {
        periodId,
        customRange,
        window: periodWindow,
    } = usePeriodParam(periodOptions, defaultPeriodId, { allowCustomDates: true });
    // A custom range is nobody's cycle, so the groups below carry no cycle line under it.
    const selectedPeriod = periodOptions.find((option) => option.id === periodId);

    const {
        data: objectives = [],
        isLoading: isLoadingObjectives,
        error: objectivesError,
    } = useGetObjectivesWithDrafts(periodWindow);
    // The filters, in the address, are not reset by a period change: switching cycles keeps the
    // same people in view, and stepping back from an objective or a pillar finds the list as it was
    // left. The pillar scope outranks them: they reach the pillars under "selected filters" alone.
    const {
        filters,
        roster,
        owners,
        statusFilter,
        pillarScope,
        heldObjectives,
        keepsDraftsOnly,
        scopedFigures,
        isTeamReach,
        filteredTeam,
    } = useObjectiveListLens(objectives, {
        isLoading: isLoadingObjectives,
        hasError: !!objectivesError,
    });
    const isOrganizationScope = pillarScope.scope === PillarScope.ORGANIZATION;

    // The company's pillars, counted company-wide: the tiles under "organization", and the catalog
    // every other scope counts again among the list's objectives. Their totals span more than the
    // objectives a reader holds, so only whoever may write objectives asks for them.
    const {
        data: companyPillars = [],
        isLoading: isLoadingPillars,
        error: pillarsError,
    } = useGetCompanyPillarSummaries(periodWindow, canReadPillarSummaries);
    const pillarSummaries = useMemo(
        () =>
            isOrganizationScope
                ? companyPillars
                : pillarSummariesAmong(companyPillars, heldObjectives),
        [isOrganizationScope, companyPillars, heldObjectives],
    );

    // The period options carry the date window: until they arrive, the queries above run
    // unfiltered, so their results must not be shown as if they were the selected period's.
    // The employee batch resolves the owner names: without it every group header would read
    // "no owner" for a beat before flipping to the real name.
    const isLoading =
        isLoadingAbilities ||
        isLoadingPeriods ||
        isLoadingObjectives ||
        owners.isLoading ||
        roster.isResolvingFilter;
    // Any failed read shows the loading error rather than a partial answer: without the cycles the
    // lists would span every cycle, without the employee batch every group would read "no owner".
    const hasError =
        arePeriodsInError || !!objectivesError || !!owners.error || roster.hasFilterError;
    // The pillar totals are read on their own and answer on their own: the row shows its own
    // placeholder, its own failure and its own empty state rather than following the list below it
    // -- which is what used to take the whole section off the page on every period change.
    // A failed cycle read is not one of them: it breaks the lens the whole page is read through, so
    // the page says so once, above, and the row steps aside rather than repeating it.
    const arePillarsLoading =
        isLoadingAbilities || isLoadingPeriods || isLoadingPillars || scopedFigures.isLoading;
    const hasPillarsError = !!pillarsError || scopedFigures.hasError;

    const renderPillarSummaries = () => {
        if (arePillarsLoading) {
            return <PillarSummariesSkeleton />;
        }

        if (hasPillarsError) {
            return <ObjectiveLoadErrorMessage dataTestId={'objectives-pillars-error'} />;
        }

        if (pillarSummaries.length > 0) {
            return (
                <PillarCarousel
                    items={pillarSummaries}
                    getKey={(pillar) => pillar.id}
                    renderItem={(pillar) => (
                        <PillarSummaryCard
                            pillar={pillar}
                            lensSearch={search}
                        />
                    )}
                    ariaLabel={t('pillars.title')}
                />
            );
        }

        // Only whoever may define the pillars is invited to; anyone else goes straight to the
        // objectives, the row left off the page rather than kept as an empty space.
        return can(Permission.ManageCompanyValues) ? <PillarsEmptyState /> : null;
    };

    const pillarRow = canReadPillarSummaries && !arePeriodsInError && renderPillarSummaries();
    // Under "organization" the tiles count the whole company, the list only what the reader may
    // open: an empty list then says whose it is rather than contradicting the tiles above it.
    const doTilesCountBeyondList =
        isOrganizationScope && companyPillars.some(({ objectivesCount }) => objectivesCount > 0);

    const renderOwnerGroups = () => {
        if (objectives.length === 0) {
            return (
                <p className={EMPTY_STATE_TEXT}>
                    {t(
                        doTilesCountBeyondList
                            ? 'objectives.byOwner.noObjectivesWithinReach'
                            : 'objectives.byOwner.noObjectives',
                    )}
                </p>
            );
        }

        if (statusFilter.groups.length === 0) {
            return (
                <p className={EMPTY_STATE_TEXT}>
                    {t(
                        emptyListMessageKey({
                            hasObjectivesBeforeStatusFilter: owners.groups.length > 0,
                            isAttributeFilterActive: roster.isFilterActive,
                            isTeamReach,
                        }),
                    )}
                </p>
            );
        }

        return (
            // 64px between owner groups, more air above each group header than the 48px rhythm
            // used elsewhere. A flex gap never adds space before the first child, so the first
            // group stays flush under the toolbar and only the space above the next ones grows.
            <div className={'flex flex-col gap-[64px]'}>
                {statusFilter.groups.map((group) => (
                    <OwnerGroup
                        key={group.ownerId ?? 'none'}
                        group={group}
                        employeesById={owners.employeesById}
                        subtitle={
                            selectedPeriod &&
                            t('objectives.groups.cycle', { label: selectedPeriod.label })
                        }
                    />
                ))}
            </div>
        );
    };

    const renderObjectives = () => {
        if (isLoading) {
            return <ObjectiveSectionLoader dataTestId={'objectives-loader'} />;
        }

        if (hasError) {
            return <ObjectiveLoadErrorMessage />;
        }

        return (
            <div
                className={OWNER_LIST_PANEL}
                data-testid={'objectives-by-owner-section'}
            >
                {renderOwnerGroups()}
            </div>
        );
    };

    return (
        <Container
            className={OBJECTIVES_PAGE_LAYOUT.shell}
            dataTestId={'objectives-page'}
        >
            <ObjectivesHeader strategicVision={strategicVision} />

            {/* The pillar cards sit straight on the page background -- no section card, no
                section title, exactly like the reference. The row keeps its place through a period
                change: what is being read shows inside it, never by taking it off the page. */}
            {pillarRow && (
                <div
                    className={'obj-page-section-top flex flex-col'}
                    data-testid={'objectives-pillars-section'}
                >
                    {/* A company known to have no pillar has none to scope: the row then only
                        invites whoever may define them. */}
                    {(isLoadingPillars || companyPillars.length > 0) && (
                        <PillarScopeBar
                            pillarScope={pillarScope}
                            subject={'pillars'}
                            keepsDraftsOnly={keepsDraftsOnly}
                            filteredTeam={filteredTeam}
                        />
                    )}

                    {pillarRow}
                </div>
            )}

            <div className={'obj-page-section-top flex flex-col gap-6'}>
                <ObjectiveListToolbar
                    filters={filters}
                    company={company}
                    canNarrowRoster={canManageOthersObjectives}
                    statusMenu={statusFilter.menuGroup}
                    periodAddon={<ObjectivesCycleHelp />}
                />

                <ObjectivesActionsPanel
                    canCreateObjectives={canCreateObjectives}
                    canManageOthersObjectives={canManageOthersObjectives}
                    periodId={periodId}
                    customRange={customRange}
                />
            </div>

            {renderObjectives()}
        </Container>
    );
}
